/**
 * Manager PA theme system - mirrors the client app's v4 look (RoniDoc green
 * accent) with its own light / dark / follow-device control, persisted in
 * localStorage('pa-settings'). Independent of the firm admin panel's theme:
 * while the PA is mounted it owns the <html> theme class, and restores the
 * panel's previous class on unmount.
 */
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ManagerThemeMode = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'pa-settings';

// RoniDoc green accent (matches the client app v4 palette).
export const PA_ACCENT = { primary: '#35AD88', hover: '#1E9D77', text: '#1E9D77', tint: '#E8F8F1' };

interface ManagerThemeCtx {
  themeMode: ManagerThemeMode;
  setThemeMode: (m: ManagerThemeMode) => void;
  resolvedTheme: 'light' | 'dark';
}

const Ctx = createContext<ManagerThemeCtx | null>(null);

function readMode(): ManagerThemeMode {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const v = JSON.parse(raw).themeMode;
      if (v === 'light' || v === 'dark' || v === 'system') return v;
    }
  } catch { /* ignore */ }
  return 'system';
}

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;
}

export function ManagerThemeProvider({ children }: { children: ReactNode }) {
  const [themeMode, setMode] = useState<ManagerThemeMode>(readMode);
  const [sysDark, setSysDark] = useState<boolean>(systemPrefersDark);

  // Track the device preference so 'system' mode reacts live.
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSysDark(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  const resolvedTheme: 'light' | 'dark' =
    themeMode === 'system' ? (sysDark ? 'dark' : 'light') : themeMode;

  // Own the <html> theme class while the PA is mounted; restore on unmount.
  useEffect(() => {
    const html = document.documentElement;
    const prev = html.classList.contains('dark') ? 'dark' : 'light';
    html.classList.remove('light', 'dark');
    html.classList.add(resolvedTheme);
    return () => {
      html.classList.remove('light', 'dark');
      html.classList.add(prev);
    };
  }, [resolvedTheme]);

  const setThemeMode = (m: ManagerThemeMode) => {
    setMode(m);
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const obj = raw ? JSON.parse(raw) : {};
      obj.themeMode = m;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
    } catch { /* ignore */ }
  };

  return <Ctx.Provider value={{ themeMode, setThemeMode, resolvedTheme }}>{children}</Ctx.Provider>;
}

export function useManagerTheme(): ManagerThemeCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useManagerTheme must be used within ManagerThemeProvider');
  return c;
}

/** Green accent CSS variables for the PA root, flipped for dark mode like v4. */
export function paAccentVars(resolvedTheme: 'light' | 'dark'): React.CSSProperties {
  return {
    '--v4-accent': PA_ACCENT.primary,
    '--v4-accent-hover': PA_ACCENT.hover,
    '--v4-accent-text': resolvedTheme === 'dark' ? PA_ACCENT.primary : PA_ACCENT.text,
    '--v4-accent-tint': resolvedTheme === 'dark' ? `${PA_ACCENT.primary}24` : PA_ACCENT.tint,
    '--v4-accent-soft': resolvedTheme === 'dark' ? `${PA_ACCENT.primary}33` : `${PA_ACCENT.primary}1A`,
  } as React.CSSProperties;
}

/** Segmented Light / Dark / Auto control. */
export function ThemeToggle({ className }: { className?: string }) {
  const { themeMode, setThemeMode } = useManagerTheme();
  const opts: { mode: ManagerThemeMode; icon: typeof Sun; label: string }[] = [
    { mode: 'light', icon: Sun, label: 'Light' },
    { mode: 'dark', icon: Moon, label: 'Dark' },
    { mode: 'system', icon: Monitor, label: 'Auto' },
  ];
  return (
    <div className={cn('inline-flex rounded-xl border border-border bg-muted/40 p-1', className)}>
      {opts.map(o => (
        <button
          key={o.mode}
          onClick={() => setThemeMode(o.mode)}
          className={cn(
            'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
            themeMode === o.mode
              ? 'bg-card text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
          aria-pressed={themeMode === o.mode}
        >
          <o.icon className="h-3.5 w-3.5" />
          {o.label}
        </button>
      ))}
    </div>
  );
}
